import { useEffect, useRef, useState } from 'react';
import type { CardMarketQuote } from '../types';
import { getApiBaseUrl } from '../constants/environment';
import {
  DEFAULT_MARKET_PRICE_PROVIDER,
  normalizeMarketPriceProvider,
  type MarketPriceProvider,
} from '../constants/marketProviders';
import { moneyFractionDigits } from '../utils/displayPrice';
import { useAuth } from './useAuth';

type QuoteMap = Record<string, CardMarketQuote | null>;
const QUOTE_BATCH_SIZE = 300;
/** Stay on GET so the CDN can store the batch. POST only when the URL would be too long. */
const QUOTE_GET_URL_MAX = 8000;

// Module-level cache: provider:variant:cardId → quote or null (explicit miss).
// Lives for the lifetime of the page session; quotes refresh next visit.
const _cache: QuoteMap = {};
// In-flight deduplication: batch key → promise
const _inflight: Map<string, Promise<void>> = new Map();

function quoteCacheKey(provider: MarketPriceProvider, variant: string, cardId: string): string {
  return `${provider}:${variant}:${cardId}`;
}

/**
 * Clear client-side quote cache so the next `useMarketQuotes` fetch hits `/api/market-quotes`
 * again with fresh DB rows (e.g. after running `npm run db:sync-quotes`).
 */
export function clearMarketQuotesSessionCache(ids?: readonly string[]): void {
  if (!ids?.length) {
    for (const k of Object.keys(_cache)) delete _cache[k];
    return;
  }
  for (const key of Object.keys(_cache)) {
    if (ids.some((id) => key.endsWith(`:${id}`))) delete _cache[key];
  }
}

function quotesFromCache(
  ids: string[],
  variant: string,
  provider: MarketPriceProvider,
): QuoteMap {
  return Object.fromEntries(ids.map((id) => [id, _cache[quoteCacheKey(provider, variant, id)] ?? null]));
}

type QuoteWaiter = {
  ids: string[];
  variant: string;
  provider: MarketPriceProvider;
  resolve: () => void;
  reject: (error: unknown) => void;
};

/** Gallery cards each call this in the same commit. Fold them into one request. */
const quoteWaiters: QuoteWaiter[] = [];
let quoteFlushScheduled = false;

function flushQuoteWaiters(): void {
  quoteFlushScheduled = false;
  const batch = quoteWaiters.splice(0);
  const groups = new Map<string, {
    variant: string;
    provider: MarketPriceProvider;
    ids: Set<string>;
    waiters: QuoteWaiter[];
  }>();
  for (const waiter of batch) {
    const key = `${waiter.provider}:${waiter.variant}`;
    let group = groups.get(key);
    if (!group) {
      group = { variant: waiter.variant, provider: waiter.provider, ids: new Set(), waiters: [] };
      groups.set(key, group);
    }
    for (const id of waiter.ids) group.ids.add(id);
    group.waiters.push(waiter);
  }
  for (const group of groups.values()) {
    void fetchQuotesUnbatched([...group.ids], group.variant, group.provider)
      .then(() => {
        for (const waiter of group.waiters) waiter.resolve();
      })
      .catch((error: unknown) => {
        for (const waiter of group.waiters) waiter.reject(error);
      });
  }
}

async function fetchQuotes(
  ids: string[],
  variant: string,
  provider: MarketPriceProvider,
): Promise<QuoteMap> {
  if (!ids.length) return {};
  const missing = ids.filter((id) => !(quoteCacheKey(provider, variant, id) in _cache));
  if (!missing.length) return quotesFromCache(ids, variant, provider);
  await new Promise<void>((resolve, reject) => {
    quoteWaiters.push({ ids: missing, variant, provider, resolve, reject });
    if (!quoteFlushScheduled) {
      quoteFlushScheduled = true;
      queueMicrotask(flushQuoteWaiters);
    }
  });
  return quotesFromCache(ids, variant, provider);
}

async function fetchQuotesUnbatched(
  ids: string[],
  variant: string,
  provider: MarketPriceProvider,
): Promise<QuoteMap> {
  if (!ids.length) return {};

  // Sorted so pack boundaries depend only on which ids are missing, not on the
  // caller's order: otherwise adding a card or re-sorting the grid re-partitions
  // every pack into new URLs and misses the CDN entry for unchanged quotes.
  const missing = ids
    .filter((id) => !(quoteCacheKey(provider, variant, id) in _cache))
    .sort();
  if (!missing.length) {
    return Object.fromEntries(ids.map((id) => [id, _cache[quoteCacheKey(provider, variant, id)] ?? null]));
  }

  const chunks: string[][] = [];
  for (let i = 0; i < missing.length; i += QUOTE_BATCH_SIZE) {
    chunks.push(missing.slice(i, i + QUOTE_BATCH_SIZE));
  }

  const base = getApiBaseUrl();
  const packed: string[][] = [];
  for (const chunk of chunks) {
    let current: string[] = [];
    for (const id of chunk) {
      const trial = [...current, id];
      const params = new URLSearchParams({
        ids: trial.join(','),
        variant,
        provider,
      });
      const getUrl = `${base}/api/market-quotes?${params.toString()}`;
      if (current.length && getUrl.length > QUOTE_GET_URL_MAX) {
        packed.push(current);
        current = [id];
      } else {
        current = trial;
      }
    }
    if (current.length) packed.push(current);
  }

  await Promise.all(
    packed.map(async (chunk) => {
      const batchKey = `${provider}:${variant}:${chunk.slice().sort().join(',')}`;
      if (_inflight.has(batchKey)) {
        await _inflight.get(batchKey);
      } else {
        const promise: Promise<void> = (async () => {
          try {
            // Prefer GET so Vercel CDN can absorb repeat quote batches (POST is uncacheable).
            const sortedIds = chunk.slice().sort();
            const params = new URLSearchParams({
              ids: sortedIds.join(','),
              variant,
              provider,
            });
            const getUrl = `${getApiBaseUrl()}/api/market-quotes?${params.toString()}`;
            const res =
              getUrl.length <= QUOTE_GET_URL_MAX
                ? await fetch(getUrl)
                : await fetch(`${getApiBaseUrl()}/api/market-quotes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids: chunk, variant, provider }),
                  });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = (await res.json()) as { quotes: QuoteMap };
            for (const [id, q] of Object.entries(data.quotes)) {
              _cache[quoteCacheKey(provider, variant, id)] = q;
            }
          } catch {
            // On fetch failure, cache null for this chunk to avoid retries.
            for (const id of chunk) {
              const key = quoteCacheKey(provider, variant, id);
              if (!(key in _cache)) _cache[key] = null;
            }
          }
        })();
        _inflight.set(batchKey, promise);
        try {
          await promise;
        } finally {
          _inflight.delete(batchKey);
        }
      }
    }),
  );

  return Object.fromEntries(ids.map((id) => [id, _cache[quoteCacheKey(provider, variant, id)] ?? null]));
}

/**
 * Batch-fetch cached market quotes for a list of card IDs.
 * Deduplicates in-flight requests and caches results for the session.
 * Fails silently — returns null for any card without a cached quote.
 *
 * @param cardIds Array of internal card IDs to look up.
 * @param variant 'standard' (default) or 'foil'.
 */
/** Sync read of session quote cache — null when any id is still uncached. */
function peekQuotesFromCache(
  ids: readonly string[],
  variant: string,
  provider: MarketPriceProvider,
): QuoteMap | null {
  if (!ids.length) return {};
  const out: QuoteMap = {};
  for (const id of ids) {
    const cacheKey = quoteCacheKey(provider, variant, id);
    if (!(cacheKey in _cache)) return null;
    out[id] = _cache[cacheKey] ?? null;
  }
  return out;
}

export function useMarketQuotes(
  cardIds: string[],
  variant: 'standard' | 'foil' | 'best' = 'best',
  providerOverride?: MarketPriceProvider,
) {
  const { user } = useAuth();
  const provider = normalizeMarketPriceProvider(
    providerOverride ?? user?.marketPriceProvider ?? DEFAULT_MARKET_PRICE_PROVIDER,
  );
  const key = `${provider}:${variant}:${cardIds.slice().sort().join(',')}`;
  const seed = peekQuotesFromCache(cardIds, variant, provider);
  const [quotes, setQuotes] = useState<QuoteMap>(() => seed ?? {});
  const [loading, setLoading] = useState(() => Boolean(cardIds.length) && seed == null);
  // Seeded mounts already have the right state. Writing it again from the
  // effect re-renders every deck card that mounts with a warm quote cache.
  const prevKeyRef = useRef(seed ? key : '');

  useEffect(() => {
    if (prevKeyRef.current === key) return;

    if (!cardIds.length) {
      prevKeyRef.current = key;
      setQuotes({});
      setLoading(false);
      return;
    }

    const cached = peekQuotesFromCache(cardIds, variant, provider);
    if (cached) {
      prevKeyRef.current = key;
      setQuotes(cached);
      setLoading(false);
      return;
    }

    prevKeyRef.current = key;
    let cancelled = false;
    let finished = false;
    setLoading(true);

    fetchQuotes(cardIds, variant, provider).then((result) => {
      if (cancelled) return;
      finished = true;
      setQuotes(result);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      // Hidden gallery panels tear effects down mid-fetch. Allow the next show
      // to try again instead of treating the abandoned key as applied.
      if (!finished) prevKeyRef.current = '';
    };
  }, [key, provider, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  return { quotes, loading, provider };
}

/**
 * Format a price for display, e.g. "€1,288.88".
 * Returns null when price is null or not a finite number.
 */
export function formatPrice(price: number | null | undefined, currency = 'EUR'): string | null {
  if (price == null || !Number.isFinite(price)) return null;
  const code = currency.trim() || 'EUR';
  const digits = moneyFractionDigits(code);
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(price);
  } catch {
    return `${code} ${price.toFixed(2)}`;
  }
}

/** Single usable price point from a cached quote row, or null if nothing to price from. */
export function usableMarketPrice(q: CardMarketQuote | null | undefined): number | null {
  if (!q) return null;
  const v = q.provider === 'cardmarket'
    ? q.meanPrice ?? q.medianPrice ?? q.lowPrice
    : q.medianPrice ?? q.meanPrice ?? q.lowPrice;
  if (v == null || !Number.isFinite(v)) return null;
  return v;
}

/** CardMarket "Avg. Sell Price" (mean) or provider mean listing price. */
export function usableMarketAvgPrice(q: CardMarketQuote | null | undefined): number | null {
  if (!q) return null;
  const v = q.meanPrice ?? q.medianPrice ?? q.lowPrice;
  if (v == null || !Number.isFinite(v)) return null;
  return v;
}

export type MarketQuotePriceBasis = 'headline' | 'avg';

function priceFromQuote(
  q: CardMarketQuote | null | undefined,
  basis: MarketQuotePriceBasis,
): number | null {
  return basis === 'avg' ? usableMarketAvgPrice(q) : usableMarketPrice(q);
}

/** Prefer the requested variant's quote; fall back to the other when listings are missing. */
export function unitPriceForVariant(
  stdQuote: CardMarketQuote | null | undefined,
  foilQuote: CardMarketQuote | null | undefined,
  variant: 'standard' | 'foil',
  basis: MarketQuotePriceBasis = 'headline',
): number | null {
  const primary = variant === 'foil' ? foilQuote : stdQuote;
  const fallback = variant === 'foil' ? stdQuote : foilQuote;
  return priceFromQuote(primary, basis) ?? priceFromQuote(fallback, basis);
}

/**
 * Compute the estimated total value of a set of cards, given their quantities and quotes.
 *
 * Pass `foilQuotes` when foil and standard copies should be priced separately
 * (foil listings often differ from non-foil). Without it, one quote per card is
 * used for all copies — legacy behaviour for simple callers.
 *
 * @param cards Array of { cardId, quantity, quantityFoil? }
 * @param quotes Standard (or best) quote map from useMarketQuotes
 * @param foilQuotes Optional foil quote map — enables variant-aware roll-up
 * @returns { total: number, currency: string, coveredCount: number, totalCount: number }
 */
export function estimateTotal(
  cards: Array<{ cardId: string; quantity: number; quantityFoil?: number }>,
  quotes: QuoteMap,
  foilQuotes?: QuoteMap,
  basis: MarketQuotePriceBasis = 'headline',
): { total: number; currency: string; coveredCount: number; totalCount: number } {
  let total = 0;
  let coveredCount = 0;
  const totalCount = cards.length;
  let currency = 'EUR';

  for (const { cardId, quantity, quantityFoil = 0 } of cards) {
    const stdQty = Math.max(0, quantity - quantityFoil);

    if (foilQuotes) {
      const stdQ = quotes[cardId];
      const foilQ = foilQuotes[cardId];
      const stdPrice = unitPriceForVariant(stdQ, foilQ, 'standard', basis);
      const foilPrice = unitPriceForVariant(stdQ, foilQ, 'foil', basis);
      let lineTotal = 0;
      let priced = false;
      if (stdQty > 0 && stdPrice != null) {
        lineTotal += stdPrice * stdQty;
        priced = true;
        currency = stdQ?.currency ?? foilQ?.currency ?? currency;
      }
      if (quantityFoil > 0 && foilPrice != null) {
        lineTotal += foilPrice * quantityFoil;
        priced = true;
        currency = foilQ?.currency ?? stdQ?.currency ?? currency;
      }
      if (!priced) continue;
      coveredCount++;
      total += lineTotal;
      continue;
    }

    const qRow = quotes[cardId];
    const price = priceFromQuote(qRow, basis);
    if (price === null || !qRow) continue;
    coveredCount++;
    currency = qRow.currency;
    total += price * stdQty;
    total += price * quantityFoil;
  }

  return { total: Math.round(total * 100) / 100, currency, coveredCount, totalCount };
}

type DeckQtyLine = { cardId: string; quantity: number; quantityFoil?: number };

/**
 * Full-deck roll-up: sums price × qty for every deck line using cached provider-preferred price,
 * grouped coverage by distinct cards and copies (not collection ownership).
 */
export function estimateDeckMarketValue(deckLines: DeckQtyLine[], quotes: QuoteMap): {
  total: number;
  currency: string;
  distinctCardsPriced: number;
  distinctCardsInDeck: number;
  copiesPriced: number;
  copiesInDeck: number;
  /** Number of distinct card types priced using foil as fallback (no standard listing). */
  foilFallbackCount: number;
} {
  const distinctInDeckIds = [...new Set(deckLines.map((l) => l.cardId))];
  let copiesInDeck = 0;

  let total = 0;
  let copiesPriced = 0;
  let currency = 'EUR';

  const pricedTypeIds = new Set<string>();
  const foilFallbackIds = new Set<string>();

  for (const { cardId, quantity, quantityFoil = 0 } of deckLines) {
    const qRow = quotes[cardId];
    copiesInDeck += quantity;
    const price = usableMarketPrice(qRow);
    if (price === null || !qRow) continue;
    currency = qRow.currency;
    const stdQty = Math.max(0, quantity - quantityFoil);
    total += price * stdQty;
    total += price * quantityFoil;
    copiesPriced += quantity;
    pricedTypeIds.add(cardId);
    if (qRow.variant === 'foil') foilFallbackIds.add(cardId);
  }

  return {
    total: Math.round(total * 100) / 100,
    currency,
    distinctCardsPriced: pricedTypeIds.size,
    distinctCardsInDeck: distinctInDeckIds.length,
    copiesPriced,
    copiesInDeck,
    foilFallbackCount: foilFallbackIds.size,
  };
}
